import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  Briefcase, Plus, Edit, Trash2, Eye, Users, Clock, MapPin,
  Building2, ArrowLeft, ChevronRight, Search, Filter, X,
  CheckCircle, AlertCircle, DollarSign, Loader, Calendar,
  Zap, Award, Crown, Upload, Image as ImageIcon, Brain, Menu, LogOut,
  LayoutDashboard, ClipboardList, Trophy, UserPlus, Star, BarChart3, CreditCard
} from 'lucide-react';
import axios from 'axios';
import { CompanyBrand } from '../components/company/CompanyBrand';
import { toast } from 'sonner';
import { load } from '@cashfreepayments/cashfree-js';
import { getFrontendUrl } from '../utils/urlConfig';
import ShareButtons from '../components/ShareButtons';

const API = process.env.REACT_APP_BACKEND_URL;

const jobTypeLabels = {
  internship: 'Internship',
  full_time: 'Full-time',
  contract: 'Contract'
};

const workModeLabels = {
  remote: 'Remote',
  hybrid: 'Hybrid',
  onsite: 'On-site'
};

const experienceLevelLabels = {
  fresher: 'Fresher',
  '1_3_years': '1-3 years',
  '3_5_years': '3-5 years',
  '5_plus_years': '5+ years'
};

const CompanyJobsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showBrandingModal, setShowBrandingModal] = useState(false);
  const [uploadingBranding, setUploadingBranding] = useState(false);
  const [nationalCompetitionPrice, setNationalCompetitionPrice] = useState(9999);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  
  const [form, setForm] = useState({
    title: '',
    company_name: '',
    company_url: '',
    job_type: 'full_time',
    work_mode: 'hybrid',
    experience_level: 'fresher',
    skills_required: [],
    salary_min: '',
    salary_max: '',
    salary_currency: 'INR',
    location: '',
    description: '',
    application_deadline: '',
    external_apply_link: '',
    apply_via_codearena: true,
    perks: [],
    visibility: 'public'
  });
  
  const [skillInput, setSkillInput] = useState('');
  const [perkInput, setPerkInput] = useState('');

  const token = localStorage.getItem('company_token');

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    
    const company = localStorage.getItem('company_data');
    if (company) {
      setCompanyData(JSON.parse(company));
    }
    
    fetchJobs();
    fetchPricing();
    
    // Handle payment callback
    const orderId = searchParams.get('order_id');
    const paymentStatus = searchParams.get('status');
    if (orderId && paymentStatus) {
      handlePaymentCallback(orderId, paymentStatus);
    }
  }, [token, navigate, searchParams]);

  const handleLogout = () => {
    localStorage.removeItem('company_token');
    localStorage.removeItem('company_data');
    navigate('/company/auth');
    toast.success('Logged out successfully');
  };

  // Navigation items for sidebar
  const navItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/company/dashboard' },
    { label: 'Job Postings', icon: Briefcase, path: '/company/jobs', active: true },
    { label: 'Assessments', icon: ClipboardList, path: '/company/assessments' },
    { label: 'Hiring Challenges', icon: Trophy, path: '/company/hiring-challenges' },
    { label: 'Enterprise', icon: Crown, path: '/company/enterprise' },
    { label: 'Events', icon: Calendar, path: '/company/events' },
    { label: 'Candidate Pool', icon: UserPlus, path: '/company/candidates' },
    { label: 'Talent Search', icon: Search, path: '/company/talent-search' },
    { label: 'Shortlisted', icon: Star, path: '/company/shortlisted' },
    { label: 'Analytics', icon: BarChart3, path: '/company/analytics' },
    { label: 'Subscription', icon: CreditCard, path: '/company/subscription' },
  ];

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/api/public/company-pricing`);
      if (res.data.pricing?.national_challenge_price) {
        setNationalCompetitionPrice(res.data.pricing.national_challenge_price);
      }
    } catch (err) {
      console.error('Failed to fetch pricing');
    }
  };

  const handlePaymentCallback = async (orderId, status) => {
    try {
      const res = await axios.post(
        `${API}/api/company/competitions/verify-payment`,
        null,
        {
          params: { order_id: orderId, payment_status: status },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (status === 'success') {
        toast.success('National Competition activated successfully!');
      } else {
        toast.error('Payment failed. Please try again.');
      }
      fetchJobs();
      // Clear URL params
      navigate('/company/jobs', { replace: true });
    } catch (err) {
      toast.error('Failed to verify payment');
    }
  };

  const initiateCompetitionPayment = async (jobId) => {
    setPaymentLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/company/competitions/initiate-payment`,
        null,
        {
          params: { job_id: jobId },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (res.data.payment_session_id) {
        // Initialize Cashfree
        const cashfree = await load({ mode: 'production' });
        cashfree.checkout({
          paymentSessionId: res.data.payment_session_id,
          returnUrl: `${getFrontendUrl()}/company/jobs?order_id=${res.data.order_id}&status=success`
        });
      } else {
        // Mock flow - directly verify as success
        toast.info('Payment gateway not configured. Simulating successful payment...');
        await axios.post(
          `${API}/api/company/competitions/verify-payment`,
          null,
          {
            params: { order_id: res.data.order_id, payment_status: 'success' },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success('National Competition activated!');
        fetchJobs();
      }
      setShowUpgradeModal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const uploadBranding = async (file, type) => {
    setUploadingBranding(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('branding_type', type);
      
      await axios.post(`${API}/api/company/upload-branding`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully!`);
    } catch (err) {
      toast.error(`Failed to upload ${type}`);
    } finally {
      setUploadingBranding(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/api/company/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(res.data.jobs || []);
    } catch (err) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.location || !form.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      // Map form fields to backend schema
      const payload = {
        title: form.title,
        company_name: form.company_name?.trim() || undefined,
        company_url: form.company_url?.trim() || null,
        description: form.description,
        skills_required: form.skills_required,
        location: form.location,
        job_type: form.job_type,
        work_mode: form.work_mode,
        experience_level: form.experience_level,
        salary_min: form.salary_min ? parseInt(form.salary_min) : null,
        salary_max: form.salary_max ? parseInt(form.salary_max) : null,
        salary_currency: form.salary_currency || 'INR',
        application_deadline: form.application_deadline || null,
        external_apply_link: form.external_apply_link || null,
        apply_via_codearena: form.apply_via_codearena,
        visibility: form.visibility
      };
      
      if (editingJob) {
        await axios.put(`${API}/api/company/jobs/${editingJob.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Job updated successfully');
      } else {
        await axios.post(`${API}/api/company/jobs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Job posted successfully. It will be visible after admin approval.');
      }
      
      setShowModal(false);
      resetForm();
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) return;
    
    try {
      await axios.delete(`${API}/api/company/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job deleted');
      fetchJobs();
    } catch (err) {
      toast.error('Failed to delete job');
    }
  };

  const toggleJobStatus = async (job) => {
    try {
      await axios.put(`${API}/api/company/jobs/${job.id}`, 
        { is_active: !job.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Job ${job.is_active ? 'deactivated' : 'activated'}`);
      fetchJobs();
    } catch (err) {
      toast.error('Failed to update job status');
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      company_name: '',
      company_url: '',
      job_type: 'full_time',
      work_mode: 'hybrid',
      experience_level: 'fresher',
      skills_required: [],
      salary_min: '',
      salary_max: '',
      salary_currency: 'INR',
      location: '',
      description: '',
      application_deadline: '',
      external_apply_link: '',
      apply_via_codearena: true,
      perks: [],
      visibility: 'public'
    });
    setEditingJob(null);
    setSkillInput('');
    setPerkInput('');
  };

  const openNewJobModal = () => {
    resetForm();
    setForm(prev => ({
      ...prev,
      company_name: companyData?.company_name || '',
      company_url: companyData?.website || ''
    }));
    setShowModal(true);
  };

  const openEditModal = (job) => {
    setEditingJob(job);
    setForm({
      title: job.title || '',
      company_name: job.company_name || '',
      company_url: job.company_url || '',
      job_type: job.job_type || 'full_time',
      work_mode: job.work_mode || 'hybrid',
      experience_level: job.experience_level || 'fresher',
      skills_required: job.skills_required || job.required_skills || [],
      salary_min: job.salary_min?.toString() || '',
      salary_max: job.salary_max?.toString() || '',
      salary_currency: job.salary_currency || 'INR',
      location: job.location || '',
      description: job.description || '',
      application_deadline: job.application_deadline?.slice(0, 10) || '',
      external_apply_link: job.external_apply_link || '',
      apply_via_codearena: job.apply_via_codearena ?? true,
      perks: job.perks || [],
      visibility: job.visibility || 'public'
    });
    setShowModal(true);
  };

  const addSkill = () => {
    if (skillInput.trim() && !form.skills_required.includes(skillInput.trim())) {
      setForm(prev => ({
        ...prev,
        skills_required: [...prev.skills_required, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setForm(prev => ({
      ...prev,
      skills_required: prev.skills_required.filter(s => s !== skill)
    }));
  };

  const addPerk = () => {
    if (perkInput.trim() && !form.perks.includes(perkInput.trim())) {
      setForm(prev => ({
        ...prev,
        perks: [...prev.perks, perkInput.trim()]
      }));
      setPerkInput('');
    }
  };

  const removePerk = (perk) => {
    setForm(prev => ({
      ...prev,
      perks: prev.perks.filter(p => p !== perk)
    }));
  };

  const suggestedPerks = ['PPO Opportunity', 'Free Lunch', 'Flexible Hours', 'Work from Home', 'Health Insurance', 'Stipend', 'Certificate', 'Letter of Recommendation', 'Mentorship'];

  const formatSalary = (min, max, currency = 'INR') => {
    if (!min && !max) return 'Not disclosed';
    const fmt = (n) => n?.toLocaleString('en-IN');
    if (min && max) return `${currency} ${fmt(min)} - ${fmt(max)}`;
    if (min) return `${currency} ${fmt(min)}+`;
    return `Up to ${currency} ${fmt(max)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f3f2ef]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#0a66c2] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-[#666666]">Loading Jobs...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#f3f2ef]">
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50
        w-72 lg:w-64 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-out
        lg:transform-none h-screen overflow-y-auto
        ${sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 px-5 flex items-center justify-between border-b border-gray-200 sticky top-0 bg-white z-10">
          <Link to="/company" className="flex items-center gap-3">
            <CompanyBrand />
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)} 
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg -mr-2"
            data-testid="close-sidebar-btn"
          >
            <X size={20} className="text-[#666666]" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3">
          <p className="text-xs font-semibold text-[#666666] uppercase tracking-wider px-3 mb-2">Navigation</p>
          <div className="space-y-0.5">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all
                  ${item.active 
                    ? 'bg-[#e8f4f8] text-[#0a66c2] font-medium' 
                    : 'text-[#666666] hover:bg-gray-100 hover:text-[#191919]'
                  }
                `}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={18} strokeWidth={item.active ? 2.5 : 2} />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 bg-white">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-[#b24020] hover:bg-red-50 rounded-lg transition-colors"
            data-testid="company-logout-btn"
          >
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg -ml-2"
              data-testid="open-sidebar-btn"
            >
              <Menu size={22} className="text-[#666666]" />
            </button>
            <div>
              <p className="text-xs font-medium text-[#666666] uppercase tracking-wider hidden sm:block">Manage</p>
              <h1 className="text-lg sm:text-xl font-semibold text-[#191919]">Job Postings</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setShowBrandingModal(true)}
              className="hidden sm:flex items-center gap-2 px-4 py-2 border border-gray-200 text-[#666666] rounded-full hover:bg-gray-50 transition-colors text-sm"
              data-testid="branding-btn"
            >
              <ImageIcon size={16} />
              <span className="hidden md:inline">Branding</span>
            </button>
            <button
              onClick={openNewJobModal}
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-[#0a66c2] text-white rounded-full hover:bg-[#004182] transition-colors text-sm font-medium"
              data-testid="create-job-btn"
            >
              <Plus size={16} />
              <span className="hidden md:inline">Post New Job</span>
            </button>
            <button
              onClick={openNewJobModal}
              className="sm:hidden p-2 bg-[#0a66c2] text-white rounded-full hover:bg-[#004182] transition-colors"
              data-testid="create-job-btn-mobile"
            >
              <Plus size={20} />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-total-jobs">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#f3f2ef] rounded-lg flex items-center justify-center">
                  <Briefcase size={16} className="text-[#666666]" />
                </div>
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Total</p>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-[#191919]">{jobs.length}</p>
              <p className="text-xs sm:text-sm text-[#666666] mt-1">Jobs Posted</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-active-jobs">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#057642]/10 rounded-lg flex items-center justify-center">
                  <CheckCircle size={16} className="text-[#057642]" />
                </div>
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Active</p>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-[#057642]">{jobs.filter(j => j.is_active).length}</p>
              <p className="text-xs sm:text-sm text-[#666666] mt-1">Live Now</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-applications">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#e8f4f8] rounded-lg flex items-center justify-center">
                  <Users size={16} className="text-[#0a66c2]" />
                </div>
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Apps</p>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-[#0a66c2]">{jobs.reduce((a, j) => a + (j.applications_count || 0), 0)}</p>
              <p className="text-xs sm:text-sm text-[#666666] mt-1">Applications</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="stat-views">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-[#fff7ed] rounded-lg flex items-center justify-center">
                  <Eye size={16} className="text-[#b24020]" />
                </div>
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wider">Views</p>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-[#b24020]">{jobs.reduce((a, j) => a + (j.views_count || 0), 0)}</p>
              <p className="text-xs sm:text-sm text-[#666666] mt-1">Total Views</p>
            </div>
          </div>

        {/* Jobs List */}
        {jobs.length > 0 ? (
          <div className="space-y-4">
            {jobs.map((job) => (
              <div 
                key={job.id}
                className={`bg-white rounded-xl border p-4 sm:p-6 ${job.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}
                data-testid={`job-card-${job.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                      <h3 className="text-base sm:text-lg font-semibold text-[#191919]">{job.title}</h3>
                      {job.is_active ? (
                        <span className="px-2 py-0.5 bg-[#057642]/10 text-[#057642] text-xs rounded-full font-medium">Active</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-gray-100 text-[#666666] text-xs rounded-full">Inactive</span>
                      )}
                      {job.is_featured && (
                        <span className="px-2 py-0.5 bg-[#fff7ed] text-[#b24020] text-xs rounded-full font-medium">Featured</span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Briefcase size={14} />
                        {jobTypeLabels[job.job_type]}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {job.location} ({workModeLabels[job.work_mode]})
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign size={14} />
                        {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {experienceLevelLabels[job.experience_level]}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {job.skills_required?.slice(0, 5).map((skill, idx) => (
                        <span key={idx} className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-lg">
                          {skill}
                        </span>
                      ))}
                      {job.skills_required?.length > 5 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-lg">
                          +{job.skills_required.length - 5} more
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-blue-600 font-medium">{job.applications_count || 0} applications</span>
                      <span className="text-gray-400">{job.views_count || 0} views</span>
                      {job.application_deadline && (
                        <span className="flex items-center gap-1 text-gray-400">
                          <Calendar size={14} />
                          Deadline: {new Date(job.application_deadline).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    {/* Share Button */}
                    <ShareButtons
                      title={job.title}
                      description={job.description}
                      url={`/jobs/${job.id}`}
                      shareText={`🚀 ${job.title} at your company | ${job.location} | ${jobTypeLabels[job.job_type]} | Apply now on EvalentX!`}
                      type="job"
                    />
                    {/* National Competition Upgrade Button */}
                    {job.is_active && !job.is_paid && (
                      <button
                        onClick={() => setShowUpgradeModal(job)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium rounded-lg hover:opacity-90 transition-all"
                        title="Upgrade to National Competition"
                        data-testid={`upgrade-btn-${job.id}`}
                      >
                        <Zap size={14} />
                        Go National
                      </button>
                    )}
                    {job.is_paid && job.is_national_competition && (
                      <span className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 text-xs font-medium rounded-lg">
                        <Crown size={14} />
                        National
                      </span>
                    )}
                    <Link
                      to={`/company/jobs/${job.id}`}
                      className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      title="View Applications"
                    >
                      <Eye size={18} />
                    </Link>
                    <Link
                      to={`/company/jobs/${job.id}/ai-dashboard`}
                      className="p-2 text-purple-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      title="AI Scoring Dashboard"
                      data-testid={`ai-dashboard-btn-${job.id}`}
                    >
                      <Brain size={18} />
                    </Link>
                    <button
                      onClick={() => openEditModal(job)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => toggleJobStatus(job)}
                      className={`p-2 rounded-lg transition-colors ${
                        job.is_active 
                          ? 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                          : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                      }`}
                      title={job.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {job.is_active ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
                    </button>
                    <button
                      onClick={() => handleDelete(job.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Briefcase className="mx-auto text-gray-300 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Job Postings Yet</h3>
            <p className="text-gray-500 mb-4">Create your first job posting to start receiving applications from CodeArena developers.</p>
            <button
              onClick={openNewJobModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0a66c2] text-white rounded-full hover:bg-[#004182] font-medium"
            >
              <Plus size={18} />
              Post Your First Job
            </button>
          </div>
        )}
        </div>
      </main>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingJob ? 'Edit Job Posting' : 'Post New Job'}
              </h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Job Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="e.g., Frontend Developer"
                  required
                />
              </div>

              {/* Company Name & URL (Optional - displayed prominently on job page) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={form.company_name}
                    onChange={(e) => setForm(prev => ({ ...prev, company_name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder={companyData?.company_name || 'e.g., Acme Corp'}
                    data-testid="company-name-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Shown prominently at the top of the job page.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company URL <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="url"
                    value={form.company_url}
                    onChange={(e) => setForm(prev => ({ ...prev, company_url: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="https://yourcompany.com"
                    data-testid="company-url-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Make the company name clickable for candidates.</p>
                </div>
              </div>
              
              {/* Job Type & Work Mode */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Type *</label>
                  <select
                    value={form.job_type}
                    onChange={(e) => setForm(prev => ({ ...prev, job_type: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="internship">Internship</option>
                    <option value="full_time">Full-time</option>
                    <option value="contract">Contract</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Work Mode *</label>
                  <select
                    value={form.work_mode}
                    onChange={(e) => setForm(prev => ({ ...prev, work_mode: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="onsite">On-site</option>
                  </select>
                </div>
              </div>
              
              {/* Experience Level & Location */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Experience Level *</label>
                  <select
                    value={form.experience_level}
                    onChange={(e) => setForm(prev => ({ ...prev, experience_level: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="fresher">Fresher</option>
                    <option value="1_3_years">1-3 years</option>
                    <option value="3_5_years">3-5 years</option>
                    <option value="5_plus_years">5+ years</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., Bangalore, India"
                    required
                  />
                </div>
              </div>
              
              {/* Salary Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salary/Stipend Range (Optional)</label>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="number"
                    value={form.salary_min}
                    onChange={(e) => setForm(prev => ({ ...prev, salary_min: e.target.value }))}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Min"
                  />
                  <input
                    type="number"
                    value={form.salary_max}
                    onChange={(e) => setForm(prev => ({ ...prev, salary_max: e.target.value }))}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Max"
                  />
                  <select
                    value={form.salary_currency}
                    onChange={(e) => setForm(prev => ({ ...prev, salary_currency: e.target.value }))}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
              
              {/* Skills */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Skills Required *</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Type a skill and press Enter"
                  />
                  <button
                    type="button"
                    onClick={addSkill}
                    className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.skills_required.map((skill, idx) => (
                    <span key={idx} className="flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                      {skill}
                      <button type="button" onClick={() => removeSkill(skill)} className="hover:text-purple-900">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Application Deadline */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Application Deadline (Optional)</label>
                <input
                  type="date"
                  value={form.application_deadline}
                  onChange={(e) => setForm(prev => ({ ...prev, application_deadline: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Description *</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  placeholder="Describe the role, responsibilities, and requirements..."
                  required
                />
              </div>
              
              {/* Perks & Benefits */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Perks & Benefits</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {suggestedPerks.filter(p => !form.perks.includes(p)).slice(0, 6).map((perk, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, perks: [...prev.perks, perk] }))}
                      className="px-3 py-1 bg-green-50 text-green-700 text-sm rounded-lg hover:bg-green-100"
                    >
                      + {perk}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={perkInput}
                    onChange={(e) => setPerkInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPerk())}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Add custom perk"
                  />
                  <button
                    type="button"
                    onClick={addPerk}
                    className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.perks.map((perk, idx) => (
                    <span key={idx} className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                      {perk}
                      <button type="button" onClick={() => removePerk(perk)} className="hover:text-green-900">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Application Method */}
              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.apply_via_codearena}
                    onChange={(e) => setForm(prev => ({ ...prev, apply_via_codearena: e.target.checked }))}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Allow applications via CodeArena</span>
                </label>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">External Apply Link (Optional)</label>
                  <input
                    type="url"
                    value={form.external_apply_link}
                    onChange={(e) => setForm(prev => ({ ...prev, external_apply_link: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="https://careers.yourcompany.com/job/..."
                  />
                </div>
              </div>

              {/* Public/Private Toggle */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Visibility Settings</label>
                <div className="flex items-center gap-4">
                  <label className={`flex-1 flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    form.visibility === 'public' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="visibility"
                      checked={form.visibility === 'public'}
                      onChange={() => setForm(prev => ({ ...prev, visibility: 'public' }))}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Public Listing</span>
                      <p className="text-xs text-gray-500">Visible on Job Board</p>
                    </div>
                  </label>
                  <label className={`flex-1 flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    form.visibility === 'private' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="visibility"
                      checked={form.visibility === 'private'}
                      onChange={() => setForm(prev => ({ ...prev, visibility: 'private' }))}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Private</span>
                      <p className="text-xs text-gray-500">Share via link only</p>
                    </div>
                  </label>
                </div>
                {form.visibility === 'private' && (
                  <p className="text-xs text-amber-600 flex items-center gap-1 mt-2">
                    <AlertCircle size={12} />
                    Private jobs won't appear on the public job board
                  </p>
                )}
              </div>
              
              {/* Submit */}
              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : (editingJob ? 'Update Job' : 'Post Job')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* National Competition Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Crown className="text-white" size={32} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Go National! 🚀</h2>
              <p className="text-gray-500">
                Upgrade "{showUpgradeModal.title}" to a National Hiring Competition for maximum visibility
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="text-center mb-4">
                <span className="text-3xl font-bold text-amber-600">₹{nationalCompetitionPrice.toLocaleString()}</span>
                <span className="text-gray-500">/one-time</span>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="text-green-500" size={16} />
                  Featured on Student Job Board
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="text-green-500" size={16} />
                  National visibility across all colleges
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="text-green-500" size={16} />
                  "National Competition" badge
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="text-green-500" size={16} />
                  30 days featured placement
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="text-green-500" size={16} />
                  Priority in search results
                </li>
              </ul>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(null)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50"
                disabled={paymentLoading}
              >
                Cancel
              </button>
              <button
                onClick={() => initiateCompetitionPayment(showUpgradeModal.id)}
                disabled={paymentLoading}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-upgrade-btn"
              >
                {paymentLoading ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Processing...
                  </>
                ) : (
                  <>
                    <Zap size={18} />
                    Pay ₹{nationalCompetitionPrice.toLocaleString()}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company Branding Modal */}
      {showBrandingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Company Branding</h2>
              <button onClick={() => setShowBrandingModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Logo Upload */}
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center">
                    <ImageIcon className="text-gray-400" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">Company Logo</h3>
                    <p className="text-sm text-gray-500">Square image, recommended 200x200px</p>
                  </div>
                  <label className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg cursor-pointer hover:bg-purple-200">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && uploadBranding(e.target.files[0], 'logo')}
                    />
                    {uploadingBranding ? <Loader className="animate-spin" size={18} /> : <Upload size={18} />}
                  </label>
                </div>
              </div>
              
              {/* Banner Upload */}
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center">
                    <ImageIcon className="text-gray-400" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">Banner Image</h3>
                    <p className="text-sm text-gray-500">Wide image, recommended 1200x400px</p>
                  </div>
                  <label className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg cursor-pointer hover:bg-purple-200">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && uploadBranding(e.target.files[0], 'banner')}
                    />
                    <Upload size={18} />
                  </label>
                </div>
              </div>
              
              {/* Poster Upload */}
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center">
                    <ImageIcon className="text-gray-400" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">Competition Poster</h3>
                    <p className="text-sm text-gray-500">Portrait image, recommended 800x1200px</p>
                  </div>
                  <label className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg cursor-pointer hover:bg-purple-200">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && uploadBranding(e.target.files[0], 'poster')}
                    />
                    <Upload size={18} />
                  </label>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setShowBrandingModal(false)}
              className="w-full mt-6 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyJobsPage;
